import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../AuthContext';
import { useTheme } from '../../ThemeContext';
import apiFetch from '../../apiClient';
import Botao from '../egora/Botao';
import { listaDe, useAlertas, nomeDoProcesso, detalheDoProcesso } from './dadosCasca';
import SinoDeNotificacoes from './SinoDeNotificacoes';
import { useAbrirFicha } from './abrirFicha';
import './casca.css';

/** Tira pontos, traços e barras: "5003127-41.2025" e "5003127412025" casam. */
function normalizar(texto) {
  return String(texto || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[.\-/\s]/g, '');
}

async function buscarJson(caminho) {
  const res = await apiFetch(caminho);
  const corpo = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(`Falha ao buscar ${caminho}`);
  return corpo;
}

/** "Venceu em 29/10", "Vence hoje" ou "Vence em 04/11" para um prazo do cartão de alertas. */
function quandoDoPrazo(grupo, iso) {
  const [, m, d] = String(iso || '').slice(0, 10).split('-');
  if (grupo === 'hoje') return 'Vence hoje';
  return grupo === 'atrasados' ? `Venceu em ${d}/${m}` : `Vence em ${d}/${m}`;
}

/**
 * Topo da área de conteúdo (Home.dc.html): busca global, tema e sino.
 *
 * A busca usa endpoints que já existem: os processos que a pessoa pode ver
 * (a mesma lista da tela de Processos, com o filtro de sigilo do servidor), a
 * busca de clientes do CRM e os prazos dos alertas (já com o privado e o sigilo). Documentos ainda não entram:
 * não há endpoint de busca de documentos (pendência de backend).
 */
export default function CascaTopo() {
  const { usuarioLogado, temPermissao } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const abrirFicha = useAbrirFicha();
  const escuro = theme === 'dark';

  const [busca, setBusca] = useState('');
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [sinoAberto, setSinoAberto] = useState(false);
  const [termoClientes, setTermoClientes] = useState('');

  const termo = busca.trim();
  const termoNormal = normalizar(termo);
  const buscando = buscaAberta && termoNormal.length > 1;

  // Clientes: a busca é no servidor; espera a pessoa parar de digitar.
  useEffect(() => {
    const t = setTimeout(() => setTermoClientes(termo), 300);
    return () => clearTimeout(t);
  }, [termo]);

  const podeProcessos = temPermissao('juridico') || temPermissao('operacao');
  const podeClientes = temPermissao('crm');

  const qProcessos = useQuery({
    queryKey: ['casca', 'busca', 'processos', usuarioLogado?.id],
    queryFn: () => buscarJson('/api/juridico/listar?per_page=200'),
    enabled: buscando && podeProcessos,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
  const qClientes = useQuery({
    queryKey: ['casca', 'busca', 'clientes', termoClientes],
    queryFn: () => buscarJson(`/api/crm/listar?per_page=4&search=${encodeURIComponent(termoClientes)}`),
    enabled: buscando && podeClientes && termoClientes.length > 1,
    staleTime: 60 * 1000,
    retry: false,
  });
  const { prazos: prazosDosAlertas } = useAlertas();

  const grupos = useMemo(() => {
    if (!buscando) return [];
    const resultado = [];
    const processos = listaDe(qProcessos.data)
      .filter((p) => normalizar([p.cnj, p.referencia_interna, p.cliente_nome, p.acao, p.titulo].join(' ')).includes(termoNormal))
      .slice(0, 4)
      .map((p) => ({ chave: `p${p.id}`, icone: 'folder_open', titulo: nomeDoProcesso(p), sub: detalheDoProcesso(p), abrir: () => abrirFicha('processo', p) }));
    if (processos.length) resultado.push({ titulo: 'Processos', itens: processos });

    const clientes = (termoClientes.length > 1 ? listaDe(qClientes.data, 'clientes') : [])
      .slice(0, 4)
      .map((c) => ({ chave: `c${c.id}`, icone: c.tipo_pessoa === 'PJ' ? 'apartment' : 'person', titulo: c.nome, sub: [c.documento, c.codigo].filter(Boolean).join(', '), abrir: () => abrirFicha('cliente', c) }));
    if (clientes.length) resultado.push({ titulo: 'Clientes', itens: clientes });

    const prazos = ['atrasados', 'hoje', 'semana']
      .flatMap((grupo) => prazosDosAlertas[grupo].map((p) => ({ ...p, grupo })))
      .filter((p) => normalizar(p.titulo).includes(termoNormal))
      .slice(0, 4)
      .map((p) => ({ chave: `prazo-${p.id}`, icone: 'schedule', titulo: p.titulo, sub: quandoDoPrazo(p.grupo, p.data), abrir: () => navigate('/agenda') }));
    if (prazos.length) resultado.push({ titulo: 'Prazos', itens: prazos });
    return resultado;
  }, [buscando, qProcessos.data, qClientes.data, termoClientes, prazosDosAlertas, termoNormal, abrirFicha, navigate]);

  const carregandoBusca = buscando && ((podeProcessos && qProcessos.isPending) || (podeClientes && qClientes.isFetching));

  const fecharTudo = () => { setBuscaAberta(false); setSinoAberto(false); };

  useEffect(() => {
    if (!buscaAberta && !sinoAberto) return undefined;
    const aoApertarTecla = (e) => { if (e.key === 'Escape') fecharTudo(); };
    window.addEventListener('keydown', aoApertarTecla);
    return () => window.removeEventListener('keydown', aoApertarTecla);
  }, [buscaAberta, sinoAberto]);

  const escolher = (item) => { setBusca(''); fecharTudo(); item.abrir(); };

  return (
    <header className="sj-topo">
      <div className="sj-busca sj-ancora casca-busca-global" role="search">
        <label className="sj-oculto" htmlFor="busca-global">Buscar</label>
        <span className="sj-icone" aria-hidden="true">search</span>
        <input
          id="busca-global"
          type="search"
          role="combobox"
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Buscar processo, cliente ou número CNJ"
          value={busca}
          onChange={(e) => { setBusca(e.target.value); setBuscaAberta(true); setSinoAberto(false); }}
          onFocus={() => setBuscaAberta(true)}
          aria-expanded={buscando}
          aria-controls="resultados-busca"
        />
        {buscando && (
          <div className="sj-lista casca-busca__resultados" id="resultados-busca" role="listbox" aria-label="Resultados da busca">
            {grupos.map((g) => (
              <div key={g.titulo} role="group" aria-label={g.titulo}>
                <div className="casca-busca__grupo">{g.titulo}</div>
                {g.itens.map((item) => (
                  <Botao key={item.chave} item role="option" aria-selected={false} className="casca-busca__item" onClick={() => escolher(item)}>
                    <span className="sj-icone casca-busca__icone" aria-hidden="true">{item.icone}</span>
                    <span className="casca-busca__textos">
                      <span className="casca-busca__titulo">{item.titulo}</span>
                      {item.sub && <span className="legenda casca-busca__sub">{item.sub}</span>}
                    </span>
                  </Botao>
                ))}
              </div>
            ))}
            {grupos.length === 0 && (
              <div className="casca-busca__vazio" role="status">
                {carregandoBusca ? 'Buscando…' : `Nada encontrado para “${termo}”. Tente o número CNJ, o CPF ou parte do nome.`}
              </div>
            )}
            <div className="casca-busca__dica">Busca em processos, clientes e prazos. Dica: cole o número CNJ inteiro.</div>
          </div>
        )}
      </div>

      <div className="sj-topo__acoes">
        <Botao
          variante="fantasma"
          icone={escuro ? 'light_mode' : 'dark_mode'}
          rotulo={escuro ? 'Usar tema claro' : 'Usar tema escuro'}
          title={escuro ? 'Usar tema claro' : 'Usar tema escuro'}
          onClick={() => setTheme(escuro ? 'light' : 'dark')}
        />
        <SinoDeNotificacoes
          aberto={sinoAberto}
          onAlternar={() => { setSinoAberto((v) => !v); setBuscaAberta(false); }}
          onFechar={fecharTudo}
        />
      </div>

      {(buscando || sinoAberto) && <div className="sj-camada" onClick={fecharTudo} aria-hidden="true" />}
    </header>
  );
}
